import { getConversionFactor } from './conversionUtils.js';
import { getAVUtil, getIFMVUtil } from './getAVUtil.js';
import { usePersistedDataStore } from '../components/store';
import { toast } from '../common/toast';
import logger from './logger';

/**
 * Utility functions for handling offer threshold calculations and rounding
 */

/**
 * UNIFIED validation function that handles BOTH upside and downside validation.
 * Always works in AV terms internally - converts user input to AV, validates, returns in input terms.
 * This is the SINGLE source of truth for all offer validation (toolbar, grid, batch).
 *
 * @param {number} userInputValue - The user's input value (in AV or MV terms based on isAVMode)
 * @param {Object} row - Case/row object (EQ, RAR, MasterPetition; IFMV/AV via getIFMVUtil/getAVUtil)
 * @param {boolean} isAVMode - Whether the user input is in AV terms (true) or MV terms (false)
 * @param {boolean} showToast - Whether to show toast notifications (default: true)
 * @param {{ inputInAVTerms?: boolean, conversionFactor?: number, settlementStage?: string }} [options] - If inputInAVTerms is true, userInputValue is already AV (e.g. toolbar pre-converted MV→AV); conversion factor is not applied to the input. isAVMode still controls thresholds/toasts/MV display returns. Optional conversionFactor overrides EQ/RAR-derived factor. settlementStage 'Art7' skips the dual-filed reduction warning.
 * @returns {Object} - { adjustedValue: number, wasAdjusted: boolean, adjustmentType: string, adjustedValueAV?: number }
 */
export function validateAndAdjustOfferValue(
  userInputValue,
  row,
  isAVMode = true,
  showToast = true,
  options = {}
) {
  const inputInAVTerms = options.inputInAVTerms === true;
  const treatInputAsAV = inputInAVTerms || isAVMode;
  if (!row) throw new Error('[validateAndAdjustOfferValue] Missing row (needs EQ/RAR).');
  const propertyAssessment = getAVUtil(row);
  const ifmvRaw = getIFMVUtil(row) ?? row?.IFMV;
  const ifmv = Number(ifmvRaw);
  const isMasterPetition = row.MasterPetition === 1 || row.MasterPetition === true;


  logger.log('🔍 [UNIFIED VALIDATION] validateAndAdjustOfferValue called:', {
    userInputValue,
    ifmv,
    propertyAssessment,
    isAVMode,
    showToast,
    isMasterPetition,
  });
  if (row.EQ == null || row.RAR == null) {
    throw new Error('[validateAndAdjustOfferValue] Missing row.EQ/row.RAR.');
  }
  const eq = Number(row.EQ);
  const rar = Number(row.RAR);
  if (!Number.isFinite(eq) || !Number.isFinite(rar)) {
    throw new Error('[validateAndAdjustOfferValue] Invalid EQ/RAR (must be finite numbers).');
  }

  // Early return for null/empty values
  if (!userInputValue || userInputValue <= 0) {
    logger.log('🔍 [UNIFIED VALIDATION] No adjustment needed - early return:', { 
      userInputValue, 
      reason: !userInputValue ? 'null/zero' : 'negative'
    });
    return { adjustedValue: userInputValue, wasAdjusted: false, adjustmentType: null };
  }

  const conversionFactor = options.conversionFactor != null
    ? Number(options.conversionFactor)
    : getConversionFactor(eq, rar);
  const needsConversion = !treatInputAsAV || !isAVMode;
  if (needsConversion && (!Number.isFinite(conversionFactor) || conversionFactor <= 0)) {
    throw new Error('[validateAndAdjustOfferValue] Invalid conversionFactor derived from EQ/RAR.');
  }
  logger.log('conversionfactor', conversionFactor);

  const algorithmStage = usePersistedDataStore.getState().negotiationObj?.Stage;
  if (algorithmStage === 'BAR') {
    logger.log('🔍 [UNIFIED VALIDATION] Skipping bounding protection — algorithm run type is BAR');
    return { adjustedValue: Math.ceil(userInputValue), wasAdjusted: false, adjustmentType: null };
  }
  
  // Convert user input to AV terms for all calculations
  let userInputAV;
  if (treatInputAsAV) {
    userInputAV = userInputValue;
  } else {
    userInputAV = userInputValue * conversionFactor;
  }
  
  // UPSIDE VALIDATION - prevent exceeding maximum allowed values (skip when no assessment)
  const upsideTolerance = Math.max(1, propertyAssessment * 1e-6);
  if (userInputAV > propertyAssessment + upsideTolerance) {
    logger.log('🔍 [UNIFIED VALIDATION] UPSIDE validation triggered:', { 
      userInputAV, 
      propertyAssessment 
    });
    
    if (showToast) {
      if (isAVMode) {
        toast.warning(`Value cannot exceed Assessment Value (${propertyAssessment.toLocaleString()} AV). Adjusting to maximum allowed.`, { position: 'top-right', autoClose: 3000 });
      } else {
        const ifmvDisplay = conversionFactor > 0 ? Math.round(propertyAssessment / conversionFactor) : Math.round(ifmv) || 0;
        toast.warning(`Value cannot exceed IFMV ($${ifmvDisplay.toLocaleString()}). Adjusting to maximum allowed.`, { position: 'top-right', autoClose: 3000 });
      }
    }
    
    const avCap = Math.ceil(propertyAssessment);
    if (isAVMode) {
      return { adjustedValue: avCap, wasAdjusted: true, adjustmentType: 'upside', adjustedValueAV: avCap };
    }
    const mvResult = conversionFactor > 0 ? propertyAssessment / conversionFactor : propertyAssessment;
    return {
      adjustedValue: Math.ceil(mvResult),
      wasAdjusted: true,
      adjustmentType: 'upside',
      adjustedValueAV: avCap
    };
  }
  
  // DOWNSIDE VALIDATION - 75% threshold validation (only for high-value properties)
  // Master Petition cases skip the clamp. Dual-filed (SCAR and Art 7) still get a warning
  // unless this value is an Art 7 settlement.
  if (ifmv > 450000) {
    const minAllowedAV = isAVMode ? propertyAssessment * 0.75 : (ifmv * 0.75) * conversionFactor;
    const scarFiled = row.SCARFiled === 1 || row.SCARFiled === true || row.SCARFiled === '1';
    const isDualFiled = isMasterPetition && scarFiled;

    logger.log('🔍 [UNIFIED VALIDATION] DOWNSIDE validation check:', {
      userInputAV,
      minAllowedAV,
      isMasterPetition,
      isDualFiled,
      needsAdjustment: userInputAV < minAllowedAV
    });

    if (userInputAV < minAllowedAV) {
      const roundedMinAllowedAV = Math.ceil(minAllowedAV);
      const maxReductionLabel = isAVMode
        ? `${roundedMinAllowedAV.toLocaleString()} AV`
        : `$${Math.round(conversionFactor > 0 ? roundedMinAllowedAV / conversionFactor : roundedMinAllowedAV).toLocaleString()}`;

      if (!isMasterPetition) {
        logger.log('🔍 [UNIFIED VALIDATION] DOWNSIDE validation triggered:', {
          minAllowedAV,
          roundedMinAllowedAV
        });

        if (showToast) {
          toast.warning(
            `Value is more than 25% below IFMV. Adjusting to ${maxReductionLabel}.`,
            { position: 'top-right', autoClose: 4000 }
          );
        }

        if (isAVMode) {
          return {
            adjustedValue: roundedMinAllowedAV,
            wasAdjusted: true,
            adjustmentType: 'downside',
            adjustedValueAV: roundedMinAllowedAV
          };
        }
        const mvResult = conversionFactor > 0 ? roundedMinAllowedAV / conversionFactor : roundedMinAllowedAV;
        return {
          adjustedValue: Math.ceil(mvResult),
          wasAdjusted: true,
          adjustmentType: 'downside',
          adjustedValueAV: roundedMinAllowedAV
        };
      }

      if (isDualFiled && options.settlementStage !== 'Art7' && showToast) {
        toast.warning(
          `Unless settling at Art 7, the maximum reduction is 25% (${maxReductionLabel}).`,
          { position: 'top-right', autoClose: 6000 }
        );
      }
    }
  }
  
  // No adjustment needed
  logger.log('🔍 [UNIFIED VALIDATION] No adjustment needed - returning original input:', userInputValue);
  return { adjustedValue: Math.ceil(userInputValue), wasAdjusted: false, adjustmentType: null };
}


